import type { PropsWithChildren } from 'react'
import React, { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { IntlProvider } from 'react-intl'
import { DEFAULT_LOCALE, type SupportedLocale } from './util/locale'
import { isSupportedLocale } from './util/localeMatcher'
import { usePseudoloc } from './PseudolocContext'
// TODO(cungminh2710): Add other languages
import enMessages from './lang/en.json'

type MessageEntry = {
  defaultMessage: string
  description?: string
}

const defaultMessages: Record<string, string> = Object.fromEntries(
  Object.entries(enMessages as Record<string, MessageEntry>).map(([key, value]) => [
    key,
    value.defaultMessage,
  ]),
)

type MessageTransformer = (messages: Record<string, string>) => Record<string, string>

interface I18nProviderProps {
  messages?: Record<string, string>
}

export function I18nProvider({
  messages = defaultMessages,
  children,
}: PropsWithChildren<I18nProviderProps>) {
  const geoLocale = useAppSelector((state) => state.geo.locale)
  const locale: SupportedLocale = geoLocale && isSupportedLocale(geoLocale) ? geoLocale : DEFAULT_LOCALE
  const { mode } = usePseudoloc()
  const [transformers, setTransformers] = useState<Record<string, MessageTransformer>>({})

  useEffect(() => {
    if (mode === 'off') return

    // Note: Both transformers are loaded regardless of the active mode since
    // they live in the same module. This avoids re-importing on mode switch.
    import('./util/pseudoloc').then((module) => {
      setTransformers((prev) => ({
        ...prev,
        full: module.pseudolocalizeMessages,
        length: module.lengthPseudolocalizeMessages,
      }))
    })
  }, [mode])

  const processedMessages = useMemo(() => {
    if (mode !== 'off') {
      const transformer = transformers[mode]
      if (transformer) {
        return transformer(messages)
      }
    }
    return messages
  }, [messages, mode, transformers])

  return (
    <IntlProvider
      locale={locale}
      messages={processedMessages}
      defaultLocale={DEFAULT_LOCALE}
      onError={(err) => {
        // Suppress missing translation warnings in development
        if (err.code === 'MISSING_TRANSLATION') {
          return
        }
        console.error(err)
      }}
    >
      {children}
    </IntlProvider>
  )
}
